import React, { useState, useEffect } from 'react'
import { Button } from '../Button'
import { api } from '../../services/api'
import { UserList, UserStats, UserSearchFilters } from '../../types/user'
import { LoadingSpinner } from '../LoadingSpinner'

interface UserManagementState {
  users: UserList[]
  stats: UserStats | null
  loading: boolean
  error: string | null
  currentPage: number
  totalPages: number
  totalUsers: number
  filters: UserSearchFilters
}

export function UserManagement() {
  const [state, setState] = useState<UserManagementState>({
    users: [],
    stats: null,
    loading: true,
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    filters: {
      page: 1,
      per_page: 20
    }
  })

  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Load users and stats
  const loadData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))

      const [usersResponse, statsResponse] = await Promise.all([
        api.users.getUsers(state.filters),
        api.users.getUserStats()
      ])

      setState(prev => ({
        ...prev,
        users: usersResponse.users,
        stats: statsResponse,
        currentPage: usersResponse.page,
        totalPages: usersResponse.pages,
        totalUsers: usersResponse.total,
        loading: false
      }))
    } catch (error) {
      console.error('Failed to load user data:', error)
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load user data'
      }))
    }
  }

  useEffect(() => {
    loadData()
  }, [state.filters])

  // Handle filter changes
  const updateFilters = (newFilters: Partial<UserSearchFilters>) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters, page: 1 }
    }))
  }

  // Handle pagination
  const goToPage = (page: number) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, page }
    }))
  }

  // Handle user actions
  const handleUserAction = async (userId: string, action: string) => {
    try {
      setActionLoading(`${action}-${userId}`)

      switch (action) {
        case 'activate':
          await api.users.reactivateUser(userId)
          break
        case 'deactivate':
          if (confirm('Are you sure you want to deactivate this user?')) {
            await api.users.deactivateUser(userId)
          } else {
            return
          }
          break
        case 'verify':
          await api.users.verifyUser(userId)
          break
        case 'make-admin':
          if (confirm('Are you sure you want to grant admin privileges to this user?')) {
            await api.users.makeUserAdmin(userId)
          } else {
            return
          }
          break
        case 'revoke-admin':
          if (confirm('Are you sure you want to revoke admin privileges from this user?')) {
            await api.users.revokeUserAdmin(userId)
          } else {
            return
          }
          break
      }

      await loadData() // Reload data
    } catch (error) {
      console.error(`Failed to ${action} user:`, error)
      alert(`Failed to ${action} user`)
    } finally {
      setActionLoading(null)
    }
  }

  // Toggle user selection
  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUsers)
    if (newSelection.has(userId)) {
      newSelection.delete(userId)
    } else {
      newSelection.add(userId)
    }
    setSelectedUsers(newSelection)
  }

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString()
  }

  // Format relative time
  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return formatDate(dateString)
  }

  if (state.loading && state.users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => window.open('https://auth.elitebench.com/admin', '_blank')}
          >
            Keycloak Admin
          </Button>
          <Button>Invite User</Button>
        </div>
      </div>

      {/* Stats Cards */}
      {state.stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="text-2xl mr-3">👥</div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{state.stats.total_users}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="text-2xl mr-3">✅</div>
              <div>
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-2xl font-bold text-green-600">{state.stats.active_users}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="text-2xl mr-3">🔒</div>
              <div>
                <p className="text-sm font-medium text-gray-600">Admin Users</p>
                <p className="text-2xl font-bold text-blue-600">{state.stats.admin_users}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="text-2xl mr-3">📈</div>
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-purple-600">{state.stats.users_this_month}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search users..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => updateFilters({ search: e.target.value || undefined })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                const value = e.target.value
                updateFilters({
                  is_active: value === 'all' ? undefined : value === 'active'
                })
              }}
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                const value = e.target.value
                updateFilters({
                  is_admin: value === 'all' ? undefined : value === 'admin'
                })
              }}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin Only</option>
              <option value="user">Users Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Verification</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => {
                const value = e.target.value
                updateFilters({
                  is_verified: value === 'all' ? undefined : value === 'verified'
                })
              }}
            >
              <option value="all">All Users</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {state.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {state.error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(new Set(state.users.map(u => u.id)))
                      } else {
                        setSelectedUsers(new Set())
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.full_name || user.username}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.company || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {user.is_verified && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Verified
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.is_admin
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatRelativeTime(user.last_login)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {/* Active/Inactive toggle */}
                      <button
                        onClick={() => handleUserAction(user.id, user.is_active ? 'deactivate' : 'activate')}
                        disabled={actionLoading === `${user.is_active ? 'deactivate' : 'activate'}-${user.id}`}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          user.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } disabled:opacity-50`}
                      >
                        {actionLoading === `${user.is_active ? 'deactivate' : 'activate'}-${user.id}` ? '...' :
                         user.is_active ? 'Deactivate' : 'Activate'}
                      </button>

                      {/* Verify toggle */}
                      {!user.is_verified && (
                        <button
                          onClick={() => handleUserAction(user.id, 'verify')}
                          disabled={actionLoading === `verify-${user.id}`}
                          className="px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                        >
                          {actionLoading === `verify-${user.id}` ? '...' : 'Verify'}
                        </button>
                      )}

                      {/* Admin toggle */}
                      <button
                        onClick={() => handleUserAction(user.id, user.is_admin ? 'revoke-admin' : 'make-admin')}
                        disabled={actionLoading === `${user.is_admin ? 'revoke-admin' : 'make-admin'}-${user.id}`}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          user.is_admin
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        } disabled:opacity-50`}
                      >
                        {actionLoading === `${user.is_admin ? 'revoke-admin' : 'make-admin'}-${user.id}` ? '...' :
                         user.is_admin ? 'Revoke Admin' : 'Make Admin'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {state.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => goToPage(state.currentPage - 1)}
                disabled={state.currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => goToPage(state.currentPage + 1)}
                disabled={state.currentPage >= state.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((state.currentPage - 1) * 20) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(state.currentPage * 20, state.totalUsers)}</span> of{' '}
                  <span className="font-medium">{state.totalUsers}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => goToPage(state.currentPage - 1)}
                    disabled={state.currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, state.totalPages) }, (_, i) => {
                    let pageNum
                    if (state.totalPages <= 5) {
                      pageNum = i + 1
                    } else if (state.currentPage <= 3) {
                      pageNum = i + 1
                    } else if (state.currentPage >= state.totalPages - 2) {
                      pageNum = state.totalPages - 4 + i
                    } else {
                      pageNum = state.currentPage - 2 + i
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === state.currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}

                  <button
                    onClick={() => goToPage(state.currentPage + 1)}
                    disabled={state.currentPage >= state.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {state.loading && state.users.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4">
            <LoadingSpinner />
          </div>
        </div>
      )}
    </div>
  )
}